"use client";

import { NavigationMenu } from "@base-ui/react/navigation-menu";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Image from "next/image";
import type { ComponentProps } from "react";

import { ArticleArt } from "@/components/blog/article-art";
import { type NavigationFeatures, useNavigationFeatures } from "./nav-features";
import { PARTNER_HREF } from "./site-links";
import { ACCENT_TEXT, DISPLAY, INK, LABEL_CLS, mix } from "./theme";

/**
 * The live card beside each group's links: the latest changelog entry
 * (Product), the featured case study (Solutions), the featured article
 * (Resources) and a Mercury Labs note (Company). Full size in the desktop
 * panels, compact in the phone sheet.
 */

export type NavGroupId = "product" | "solutions" | "resources" | "company";

export const BODY_MUTED = mix("--theme-ink", 74);
export const QUIET = mix("--theme-ink", 66);

/** Inset ring for rows inside a clipping panel or scroller. */
export const FOCUS_INSET_CLS =
  "focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-2 focus-visible:outline-[color:var(--theme-ink)]";

/* Copy for the Company card, from the About page header. */
const COMPANY_LINE =
  "A London-based AI engineering studio in Eccleston Yards that designs and deploys practical AI for businesses.";

/** → for pages on this site, ↗ for the docs and partner sites. SVG, not
 *  text glyphs, so no device renders them as emoji. */
export function LinkArrow({ external = false, className = "size-4" }: { external?: boolean; className?: string }) {
  const Icon = external ? ArrowUpRight : ArrowRight;
  return (
    <Icon
      aria-hidden="true"
      focusable="false"
      className={`${className} shrink-0 transition-transform duration-200 ease-out motion-reduce:transition-none ${
        external
          ? "group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
          : "group-hover/link:translate-x-0.5"
      }`}
    />
  );
}

/** Card links render as NavigationMenu.Link inside a desktop panel (so the
 *  menu closes and keeps its roving focus) and as plain anchors in the sheet. */
function FeatureLink({
  inMenu,
  onNavigate,
  ...props
}: ComponentProps<"a"> & { inMenu: boolean; onNavigate?: () => void }) {
  if (inMenu) return <NavigationMenu.Link closeOnClick {...props} />;
  return <a {...props} onClick={onNavigate} />;
}

function CardAction({ label, external = false }: { label: string; external?: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[0.86rem] font-semibold"
      style={{ color: ACCENT_TEXT }}
    >
      {label}
      <LinkArrow external={external} className="size-3.5" />
    </span>
  );
}

/* ================================================================= */
/*  Feature cards                                                      */
/* ================================================================= */

type CardProps = { features: NavigationFeatures; compact: boolean; inMenu: boolean; onNavigate?: () => void };

function ChangelogCard({ features, compact, inMenu, onNavigate }: CardProps) {
  const entry = features.changelog;
  if (!entry) {
    return (
      <p className={compact ? "p-4 text-[0.86rem]" : "p-6 text-[0.86rem]"} style={{ color: BODY_MUTED }}>
        No changelog entries are published yet.
      </p>
    );
  }
  return (
    <FeatureLink
      inMenu={inMenu}
      onNavigate={onNavigate}
      href={`/changelog#${entry.slug}`}
      className={`group/link flex h-full flex-col ${compact ? "p-4" : "p-6"} ${FOCUS_INSET_CLS}`}
      style={{ color: INK }}
    >
      <span className={LABEL_CLS} style={{ color: QUIET }}>
        Latest update
      </span>
      <span
        className={`mt-3 block font-semibold leading-[1.25] tracking-[-0.01em] decoration-1 underline-offset-4 group-hover/link:underline ${
          compact ? "text-[0.98rem]" : "text-[1.08rem]"
        }`}
        style={DISPLAY}
      >
        {entry.title}
      </span>
      {compact ? null : (
        <span className="mt-2 block text-[0.86rem] leading-[1.55]" style={{ color: BODY_MUTED }}>
          {entry.summary}
        </span>
      )}
      <span className="mt-3 block text-[0.8rem]" style={{ color: QUIET }}>
        {entry.dateLabel}
      </span>
      <span className={compact ? "mt-3" : "mt-auto pt-5"}>
        <CardAction label="Read the changelog" />
      </span>
    </FeatureLink>
  );
}

function CaseStudyCard({ features, compact, inMenu, onNavigate }: CardProps) {
  const study = features.caseStudy;
  const heroStyle = study.hero.focus ? { objectPosition: study.hero.focus } : undefined;
  const metric = (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span
        className={`leading-none font-semibold tracking-[-0.03em] ${compact ? "text-[1.25rem]" : "text-[1.6rem]"}`}
        style={{ ...DISPLAY, color: INK }}
      >
        {study.metricValue}
      </span>
      <span className="text-[0.86rem] leading-[1.35]" style={{ color: mix("--theme-ink", 78) }}>
        {study.metricLabel}
      </span>
    </span>
  );
  const wordmark = (
    <Image
      src={study.logo.src}
      width={study.logo.width}
      height={study.logo.height}
      alt={`${study.company} logo`}
      className={`w-auto self-start opacity-85 ${compact ? "h-5" : "h-7"}`}
    />
  );

  if (compact) {
    return (
      <FeatureLink
        inMenu={inMenu}
        onNavigate={onNavigate}
        href={`/case-studies/${study.slug}`}
        className={`group/link grid grid-cols-[5rem_minmax(0,1fr)] gap-4 p-4 ${FOCUS_INSET_CLS}`}
        style={{ color: INK }}
      >
        <span className="relative block aspect-square overflow-hidden" style={{ backgroundColor: mix("--theme-ink", 6) }}>
          <Image src={study.hero.src} alt="" fill sizes="5rem" className="object-cover" style={heroStyle} />
        </span>
        <span className="flex min-w-0 flex-col gap-2">
          {wordmark}
          {metric}
          <CardAction label="Read the case study" />
        </span>
      </FeatureLink>
    );
  }

  return (
    <FeatureLink
      inMenu={inMenu}
      onNavigate={onNavigate}
      href={`/case-studies/${study.slug}`}
      className={`group/link flex h-full flex-col p-6 ${FOCUS_INSET_CLS}`}
      style={{ color: INK }}
    >
      <span className={LABEL_CLS} style={{ color: QUIET }}>
        Case study
      </span>
      <span
        className="relative mt-4 block aspect-[2/1] overflow-hidden"
        style={{ backgroundColor: mix("--theme-ink", 6) }}
      >
        <Image
          src={study.hero.src}
          alt=""
          fill
          sizes="15rem"
          className="object-cover transition-transform duration-500 ease-out group-hover/link:scale-[1.015] motion-reduce:transition-none"
          style={heroStyle}
        />
      </span>
      <span className="mt-4 block">{wordmark}</span>
      <span className="mt-3 block">{metric}</span>
      <span className="mt-1.5 block text-[0.78rem]" style={{ color: QUIET }}>
        {study.metricMethod}
      </span>
      <span className="mt-auto pt-5">
        <CardAction label="Read the case study" />
      </span>
    </FeatureLink>
  );
}

function BlogCard({ features, compact, inMenu, onNavigate }: CardProps) {
  const post = features.blog;
  const label = (
    <span className={LABEL_CLS} style={{ color: QUIET }}>
      Featured / {post.category}
    </span>
  );
  const title = (
    <span
      className={`block font-semibold leading-[1.28] tracking-[-0.01em] decoration-1 underline-offset-4 group-hover/link:underline ${
        compact ? "text-[0.98rem]" : "text-[1.05rem]"
      }`}
      style={DISPLAY}
    >
      {post.title}
    </span>
  );

  if (compact) {
    return (
      <FeatureLink
        inMenu={inMenu}
        onNavigate={onNavigate}
        href={`/blog/${post.slug}`}
        className={`group/link block p-4 ${FOCUS_INSET_CLS}`}
        style={{ color: INK }}
      >
        {label}
        <span className="mt-3 grid grid-cols-[6rem_minmax(0,1fr)] items-start gap-4">
          <span className="block overflow-hidden">
            <ArticleArt slug={post.slug} />
          </span>
          <span className="flex min-w-0 flex-col gap-1.5">
            {title}
            <span className="text-[0.8rem]" style={{ color: QUIET }}>
              {post.readTime}
            </span>
          </span>
        </span>
      </FeatureLink>
    );
  }

  return (
    <FeatureLink
      inMenu={inMenu}
      onNavigate={onNavigate}
      href={`/blog/${post.slug}`}
      className={`group/link flex h-full flex-col p-6 ${FOCUS_INSET_CLS}`}
      style={{ color: INK }}
    >
      {label}
      <span className="mt-4 block overflow-hidden">
        <ArticleArt slug={post.slug} />
      </span>
      <span className="mt-4 block">{title}</span>
      <span className="mt-2 block text-[0.82rem]" style={{ color: QUIET }}>
        {post.readTime}
      </span>
      <span className="mt-auto pt-5">
        <CardAction label="Read the article" />
      </span>
    </FeatureLink>
  );
}

/* Mercury Labs' own palette (mercurylabs.io globals.css), fixed rather than
 * themed: this card speaks for the parent studio, not the product. */
const MERCURY = {
  surface: "#11110f",
  ground: "#faf9f7",
  body: "#b9bbb5",
  quiet: "#8f918b",
} as const;

/** Static copy, so it needs no loaded data. Drawn in the Mercury Labs brand. */
function CompanyCard({ compact, inMenu, onNavigate }: Omit<CardProps, "features">) {
  return (
    <div
      className={`flex h-full flex-col ${compact ? "p-4" : "p-6"}`}
      style={{ backgroundColor: MERCURY.surface, color: MERCURY.ground }}
    >
      <p className={LABEL_CLS} style={{ color: MERCURY.quiet }}>
        Built by
      </p>
      <p
        className={`mt-3 font-semibold leading-none tracking-[-0.02em] ${compact ? "text-[1.2rem]" : "text-[1.45rem]"}`}
        style={DISPLAY}
      >
        Mercury Labs
      </p>
      <p className="mt-3 text-[0.86rem] leading-[1.6]" style={{ color: MERCURY.body }}>
        {COMPANY_LINE}
      </p>
      <p className={compact ? "mt-4" : "mt-auto pt-6"}>
        <FeatureLink
          inMenu={inMenu}
          onNavigate={onNavigate}
          href={PARTNER_HREF}
          className="group/link inline-flex min-h-11 items-center gap-1.5 border-b text-[0.86rem] font-semibold focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-4 focus-visible:outline-[#faf9f7]"
          style={{ color: MERCURY.ground, borderColor: MERCURY.ground }}
        >
          Become a partner
          <LinkArrow external className="size-3.5" />
        </FeatureLink>
      </p>
    </div>
  );
}

export function FeatureCard({
  group,
  compact,
  inMenu,
  onNavigate,
}: {
  group: NavGroupId;
  compact: boolean;
  inMenu: boolean;
  onNavigate?: () => void;
}) {
  const features = useNavigationFeatures();
  if (group === "company") {
    return <CompanyCard compact={compact} inMenu={inMenu} onNavigate={onNavigate} />;
  }
  const props = { features, compact, inMenu, onNavigate };
  if (group === "product") return <ChangelogCard {...props} />;
  if (group === "solutions") return <CaseStudyCard {...props} />;
  return <BlogCard {...props} />;
}
